/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

// Client fingerprint collection module.
// Computes a device fingerprint once at app startup and caches the result.
// The fingerprint is sent as X-Client-Context on every API request.
// The server interprets the signals; the client is a dumb collector.

let _cachedHeader: string | null = null;
let _cachedFingerprint: Record<string, unknown> | null = null;
let _initPromise: Promise<void> | null = null;

function djb2Hash(str: string): string {
	let hash = 5381;
	for (let i = 0; i < str.length; i++) {
		hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
	}
	return (hash >>> 0).toString(16);
}

async function collect(): Promise<Record<string, unknown>> {
	const ctx: Record<string, unknown> = {ts: Date.now()};
	const nav = navigator as unknown as Record<string, unknown>;
	try {
		// ── Screen & display ──
		ctx.screenWidth = window.screen?.width ?? 0;
		ctx.screenHeight = window.screen?.height ?? 0;
		ctx.colorDepth = window.screen?.colorDepth ?? 0;
		ctx.innerWidth = window.innerWidth ?? 0;
		ctx.innerHeight = window.innerHeight ?? 0;
		ctx.outerWidth = window.outerWidth ?? 0;
		ctx.outerHeight = window.outerHeight ?? 0;
		ctx.devicePixelRatio = window.devicePixelRatio ?? 1;

		// ── Navigator properties ──
		ctx.timezone = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone ?? null;
		ctx.languages = navigator?.languages ? [...navigator.languages] : [];
		ctx.hardwareConcurrency = navigator?.hardwareConcurrency ?? null;
		ctx.deviceMemory = nav?.deviceMemory ?? null;
		ctx.platform = navigator?.platform ?? null;
		ctx.touchSupport = navigator?.maxTouchPoints ?? 0;
		ctx.cookiesEnabled = navigator?.cookieEnabled ?? null;
		ctx.doNotTrack = navigator?.doNotTrack ?? null;
		ctx.webdriver = nav?.webdriver ?? false;
		ctx.pdfViewerEnabled = nav?.pdfViewerEnabled ?? null;
		ctx.pluginCount = navigator?.plugins?.length ?? 0;

		// ── Connection info ──
		try {
			const conn = nav?.connection as Record<string, unknown> | undefined;
			if (conn) {
				ctx.connectionEffectiveType = conn.effectiveType ?? null;
				ctx.connectionDownlink = conn.downlink ?? null;
				ctx.connectionRtt = conn.rtt ?? null;
			}
		} catch { /* not available */ }

		// ── Chrome object presence (absent in some headless setups) ──
		ctx.hasChrome = typeof (window as unknown as Record<string, unknown>).chrome !== 'undefined';

		// ── CDP (Chrome DevTools Protocol) artifact detection ──
		try {
			const win = window as unknown as Record<string, unknown>;
			const doc = document as unknown as Record<string, unknown>;
			ctx.hasCdcProps =
				Object.getOwnPropertyNames(win).some((k) => k.startsWith('cdc_')) ||
				Object.getOwnPropertyNames(doc).some((k) => k.startsWith('$cdc_'));
		} catch {
			ctx.hasCdcProps = false;
		}

		// ── Automation artifacts ──
		try {
			const win = window as unknown as Record<string, unknown>;
			ctx.hasAutomation =
				win.__nightmare !== undefined ||
				win._phantom !== undefined ||
				win.callPhantom !== undefined ||
				win.__selenium_unwrapped !== undefined ||
				win.__webdriver_evaluate !== undefined ||
				win.__driver_evaluate !== undefined ||
				document.querySelector('[selenium-evaluate]') !== null;
		} catch {
			ctx.hasAutomation = false;
		}

		// ── performance.now() precision (reduced = privacy mode / tor browser) ──
		try {
			const t1 = performance.now();
			const t2 = performance.now();
			ctx.perfNowPrecision = t2 - t1 < 0.005 ? 'high' : t1 % 1 === 0 ? 'reduced' : 'normal';
		} catch {
			ctx.perfNowPrecision = null;
		}

		// ── Canvas fingerprint ──
		try {
			const canvas = document.createElement('canvas');
			canvas.width = 280;
			canvas.height = 60;
			const c = canvas.getContext('2d');
			if (c) {
				c.textBaseline = 'alphabetic';
				c.fillStyle = '#f60';
				c.fillRect(125, 1, 62, 20);
				c.fillStyle = '#069';
				c.font = '11pt no-real-font-123';
				c.fillText('Cwm fjordbank glyph', 2, 15);
				c.fillStyle = 'rgba(102, 204, 0, 0.7)';
				c.font = '18pt Arial';
				c.fillText('Cwm fjordbank glyph', 4, 45);
				c.globalCompositeOperation = 'multiply';
				c.fillStyle = 'rgb(255,0,255)';
				c.beginPath();
				c.arc(50, 50, 50, 0, Math.PI * 2, true);
				c.closePath();
				c.fill();
				ctx.canvasHash = djb2Hash(canvas.toDataURL());
			}
		} catch { /* Canvas may be blocked */ }

		// ── WebGL vendor + renderer ──
		try {
			const gl = document.createElement('canvas').getContext('webgl');
			if (gl) {
				const dbg = gl.getExtension('WEBGL_debug_renderer_info');
				if (dbg) {
					ctx.webglVendor = gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL);
					ctx.webglRenderer = gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL);
				}
				ctx.webglVersion = gl.getParameter(gl.VERSION);
				ctx.webglShadingLang = gl.getParameter(gl.SHADING_LANGUAGE_VERSION);
				ctx.webglMaxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
			}
		} catch { /* WebGL unavailable */ }

		// ── Audio fingerprint hash ──
		try {
			const audioCtx = new OfflineAudioContext(1, 44100, 44100);
			const osc = audioCtx.createOscillator();
			osc.type = 'triangle';
			osc.frequency.setValueAtTime(10000, audioCtx.currentTime);
			const comp = audioCtx.createDynamicsCompressor();
			comp.threshold.setValueAtTime(-50, audioCtx.currentTime);
			comp.knee.setValueAtTime(40, audioCtx.currentTime);
			comp.ratio.setValueAtTime(12, audioCtx.currentTime);
			comp.attack.setValueAtTime(0, audioCtx.currentTime);
			comp.release.setValueAtTime(0.25, audioCtx.currentTime);
			osc.connect(comp);
			comp.connect(audioCtx.destination);
			osc.start(0);
			audioCtx.startRendering().then((buffer) => {
				const data = buffer.getChannelData(0);
				let sum = 0;
				for (let i = 4500; i < 5000; i++) sum += Math.abs(data[i]!);
				ctx.audioHash = djb2Hash(sum.toString());
			}).catch(() => { /* audio fingerprint failed */ });
			await new Promise((r) => setTimeout(r, 50));
		} catch { /* AudioContext unavailable */ }

		// ── Font detection (measure a few distinctive fonts) ──
		try {
			const baseFonts = ['monospace', 'sans-serif', 'serif'] as const;
			const testFonts = [
				'Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Verdana',
				'Trebuchet MS', 'Palatino', 'Impact', 'Comic Sans MS', 'Lucida Console',
				'Segoe UI', 'Roboto', 'Helvetica Neue', 'SF Pro',
			];
			const span = document.createElement('span');
			span.style.position = 'absolute';
			span.style.left = '-9999px';
			span.style.fontSize = '72px';
			span.textContent = 'mmmmmmmmmmlli';
			document.body.appendChild(span);

			const baseWidths: Record<string, number> = {};
			for (const base of baseFonts) {
				span.style.fontFamily = base;
				baseWidths[base] = span.offsetWidth;
			}

			const detected: Array<string> = [];
			for (const font of testFonts) {
				for (const base of baseFonts) {
					span.style.fontFamily = `"${font}", ${base}`;
					if (span.offsetWidth !== baseWidths[base]) {
						detected.push(font);
						break;
					}
				}
			}
			document.body.removeChild(span);
			ctx.fontsDetected = detected;
		} catch { /* font detection failed */ }

		// ── Speech synthesis voices ──
		try {
			const voices = window.speechSynthesis?.getVoices?.() ?? [];
			ctx.voiceCount = voices.length;
			if (voices.length > 0) {
				ctx.voiceHash = djb2Hash(voices.map((v) => `${v.name}|${v.lang}`).join(','));
			}
		} catch { /* speech synthesis unavailable */ }

		// ── Math engine fingerprint ──
		try {
			ctx.mathFp = djb2Hash([
				Math.tan(-1e300), Math.atan2(1, 2), Math.sin(1),
				Math.pow(Math.PI, -100), Math.exp(100), Math.log(2.0001),
				Math.sqrt(2), Math.E * Math.PI,
			].join(','));
		} catch { /* math fingerprint failed */ }

		// ── Error stack format (differs by engine) ──
		try {
			const err = new Error('_fp');
			ctx.stackSig = djb2Hash((err.stack ?? '').split('\n').slice(0, 3).join(''));
		} catch { /* stack fingerprint failed */ }

		// ── Storage quota ──
		try {
			if (navigator.storage?.estimate) {
				const est = await navigator.storage.estimate();
				ctx.storageQuota = est.quota ?? null;
			}
		} catch { /* storage API unavailable */ }

		// ── Notification permission ──
		try {
			ctx.notifPerm = typeof Notification !== 'undefined' ? Notification.permission : null;
		} catch { /* Notification API unavailable */ }

		// ── Screen orientation ──
		try {
			ctx.orientation = window.screen?.orientation?.type ?? null;
		} catch { /* orientation API unavailable */ }

		// ── document.fonts ──
		try {
			ctx.fontSetSize = document.fonts?.size ?? null;
		} catch { /* FontFaceSet unavailable */ }

		// ── Media preferences ──
		try {
			ctx.colorScheme = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
			ctx.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
		} catch { /* matchMedia unavailable */ }

		// ── User-Agent Client Hints ──
		try {
			const uaData = nav?.userAgentData as Record<string, unknown> | undefined;
			if (uaData) {
				ctx.uaBrands = (uaData.brands as Array<{brand: string; version: string}> | undefined)
					?.map((b) => `${b.brand}/${b.version}`)
					?.join(',') ?? null;
				ctx.uaMobile = uaData.mobile ?? null;
				ctx.uaPlatform = uaData.platform ?? null;
			}
		} catch { /* UA Client Hints unavailable */ }

		// ── Date formatting (engine-specific) ──
		try {
			ctx.dateFmt = djb2Hash(new Date(0).toString());
		} catch { /* date format fingerprint failed */ }

	} catch {
		// Best-effort collection
	}
	return ctx;
}

/** Returns the cached base64-encoded fingerprint header value, or null if not yet computed. */
export function getClientContextHeader(): string | null {
	return _cachedHeader;
}

/** Returns the cached raw fingerprint object, or null if not yet computed. */
export function getClientFingerprint(): Record<string, unknown> | null {
	return _cachedFingerprint;
}

/** Compute the fingerprint eagerly. Call once at app startup. Safe to call multiple times. */
export function initializeClientFingerprint(): Promise<void> {
	if (!_initPromise) {
		_initPromise = collect().then((fp) => {
			_cachedFingerprint = fp;
			try {
				_cachedHeader = btoa(JSON.stringify(fp));
			} catch {
				_cachedHeader = null;
			}
		}).catch(() => {
			// Non-fatal — missing fingerprint is itself a signal to the server.
		});
	}
	return _initPromise;
}
